import React, { useEffect, useRef, useState } from 'react';

import { AddRounded } from '@mui/icons-material';
import { Autocomplete, Box, CircularProgress, FormControl, TextField } from '@mui/material';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { matchSorter } from 'match-sorter';
import useSWR from 'swr';

import Button from '@components/Button';
import { CreateProjectModal } from '@components/dashboard/CreateProjectModal';
import { ProjectColorDot } from '@components/dashboard/ProjectColorSelect';
import FieldError from '@components/forms/FieldError';

import { PermissionName } from '@models/Permission';
import { Project } from '@models/Project';

import Endpoints from '@services/Endpoints';
import { PaginationResponse } from '@services/EndpointUtil';

import { hasPermission } from '@util/PermissionUtil';

const DEFAULT_LIMIT = 100;
const NONE_VALUE = 'NONE';

type Props = {
    onChange?: (project: string | null) => void;
    value?: string | null;
    label?: string;
    name: string;
    error?: string | null | boolean;
    defaultMostRecent?: boolean;
    disabled?: boolean;
    showAddProject?: boolean;
    requiredPermissions?: PermissionName[];
    matchAnyPermissions?: boolean;
    hidePermissionDenied?: boolean;
    disablePermissionDenied?: boolean;
    showNone?: boolean;
    noneLabel?: string;
    onProjectCreated?: (project: Project) => Promise<void> | void;
};

const ProjectSelect: React.FunctionComponent<Props> = ({
    onChange,
    value,
    label,
    name,
    error,
    defaultMostRecent = true,
    disabled = false,
    showAddProject = false,
    requiredPermissions = [],
    matchAnyPermissions = false,
    hidePermissionDenied = false,
    disablePermissionDenied = true,
    showNone = false,
    noneLabel = 'No project',
    onProjectCreated,
}: Props) => {
    const {
        data: projectsData,
        error: projectsError,
        mutate,
    } = useSWR<PaginationResponse<Project>>(`${Endpoints.lab.projects()}?limit=${DEFAULT_LIMIT}`);
    const loadedRef = useRef(false);
    const [createProjectOpen, setCreateProjectOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState<string>('');

    const isDisabled = (project: Project) => {
        if (requiredPermissions.length === 0 || !disablePermissionDenied) {
            return false;
        }
        return !hasPermission(project, { requires: requiredPermissions, any: matchAnyPermissions });
    };

    const isHidden = (project: Project) => {
        if (requiredPermissions.length === 0 || !hidePermissionDenied) {
            return false;
        }
        return !hasPermission(project, { requires: requiredPermissions, any: matchAnyPermissions });
    };

    const loading = !projectsData && !projectsError;
    const allProjects = projectsData?.items ?? [];
    const projects = allProjects.filter((p) => !isHidden(p)).sort((a, b) => a.name.localeCompare(b.name)); // Sort in ascending order

    // Create a "None" option if showNone is true (only used in contexts where no project selection is valid)
    // This is not used in the "create your experiment" workflow where a project is required
    const noneOption = showNone
        ? ({
              uuid: NONE_VALUE,
              name: noneLabel,
              color: '#CCCCCC',
              pluto_id: '',
              experiments_count: 0,
          } as unknown as Project)
        : null;

    // Find the selected project
    const selectedProject = value
        ? value === NONE_VALUE && noneOption
            ? noneOption
            : projects.find((p) => p.uuid === value)
        : null;

    // Default to most recent project if no value is provided
    useEffect(() => {
        if (!value && defaultMostRecent && !loading && projects.length > 0 && !loadedRef.current) {
            onChange?.(projects[0].uuid);
            loadedRef.current = true;
        }
    }, [projects, loading, defaultMostRecent, value, onChange]);

    const showNewProjectModal = () => {
        setCreateProjectOpen(true);
    };

    const handleProjectCreated = async (project: Project) => {
        await mutate((data) => ({ count: (data?.count ?? 0) + 1, items: [project, ...(data?.items ?? [])] }));
        onProjectCreated?.(project);
    };

    const filterOptions = (options: Project[], state: FilterOptionsState<Project>): Project[] => {
        const filtered = matchSorter(options, state.inputValue, {
            keys: ['name'],
            threshold: matchSorter.rankings.CONTAINS,
        });

        // Always keep the "None" option at the top if it exists and showNone is true
        if (noneOption && !state.inputValue) {
            return [noneOption, ...filtered.filter((p) => p.uuid !== NONE_VALUE)];
        }

        return filtered;
    };

    return (
        <>
            <div className="form-field">
                <div className="flex flex-row items-baseline justify-between">
                    <span className="field-label w-unset">{label ?? 'Select a project'}</span>
                    {showAddProject && (
                        <Button
                            variant="text"
                            onClick={showNewProjectModal}
                            startIcon={<AddRounded fontSize="small" />}
                            className="shrink-0 whitespace-nowrap"
                            disabled={disabled}
                            size="small"
                        >
                            Add Project
                        </Button>
                    )}
                </div>
                <FormControl variant="outlined" fullWidth error={!!error}>
                    <Autocomplete
                        id={`project-select-${name}`}
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        value={selectedProject ?? null}
                        onChange={(_, newValue) => {
                            if (!newValue) {
                                onChange?.(null);
                            } else {
                                onChange?.(newValue.uuid);
                            }
                        }}
                        inputValue={inputValue}
                        onInputChange={(_, newInputValue) => {
                            setInputValue(newInputValue);
                        }}
                        isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                        getOptionDisabled={isDisabled}
                        filterOptions={filterOptions}
                        options={showNone && noneOption ? [noneOption, ...projects] : projects}
                        loading={loading}
                        noOptionsText="No projects found"
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option, { inputValue }) => {
                            const matches = match(option.name, inputValue, { insideWords: true });
                            const parts = parse(option.name, matches);

                            return (
                                <Box component="li" {...props}>
                                    <div className="flex max-w-sm flex-row items-center justify-start whitespace-normal sm:max-w-lg">
                                        <ProjectColorDot color={option.color} className="mr-2 shrink-0" />
                                        <div>
                                            {parts.map((part, index) => (
                                                <span
                                                    key={index}
                                                    style={{
                                                        fontWeight: part.highlight ? 600 : 400,
                                                        color: part.highlight ? '#4f46e5' : 'inherit',
                                                    }}
                                                >
                                                    {part.text}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Search projects..."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        disabled={disabled}
                    />
                    {error && <FieldError>{error}</FieldError>}
                </FormControl>
            </div>
            <CreateProjectModal
                showAddExperiment={false}
                onCreated={handleProjectCreated}
                open={createProjectOpen}
                onClose={() => setCreateProjectOpen(false)}
            />
        </>
    );
};

export default ProjectSelect;
